// ---------------------------------------------------------------------------
// TanStack Query hooks for audio upload & translation API
// ---------------------------------------------------------------------------

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { QuestionTextTranslation, AddTranslationRequest, UpdateTranslationRequest } from '@/types/question';
import type { UploadUrlRequest, UploadUrlResponse, ConfirmUploadRequest } from '@/types/audio';

const TRANSLATIONS_KEY = 'translations';

export function useTranslations(questionId: number | undefined) {
  return useQuery({
    queryKey: [TRANSLATIONS_KEY, questionId],
    queryFn: async () => {
      const res = await api.get<{ items: QuestionTextTranslation[] }>(`/translations?questionId=${questionId}`);
      return res.items;
    },
    enabled: !!questionId,
  });
}

export function useRequestUploadUrl() {
  return useMutation({
    mutationFn: (data: UploadUrlRequest) =>
      api.post<UploadUrlResponse>('/audio/upload-url', data),
  });
}

export function useConfirmUpload() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: ConfirmUploadRequest) =>
      api.post<void>('/audio/confirm', data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: [TRANSLATIONS_KEY, variables.questionId] });
    },
  });
}

export function useAddTranslation(questionId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: AddTranslationRequest) =>
      api
        .post<{ entries: QuestionTextTranslation[] }>(`/translation`, {
          entries: [{ questionId, ...data }],
        })
        .then((r) => r.entries[0]),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TRANSLATIONS_KEY, questionId] });
    },
  });
}

export function useUpdateTranslation(questionId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ languageCode, ...data }: UpdateTranslationRequest & { languageCode: string }) =>
      api
        .patch<{ entries: QuestionTextTranslation[] }>(`/translation`, {
          entries: [{ questionId, languageCode, ...data }],
        })
        .then((r) => r.entries[0]),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TRANSLATIONS_KEY, questionId] });
    },
  });
}
